import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import { listMovementTypes } from "@/infrastructure/movement/movementService";
import type { MovementSubtype, MovementType } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

export function SubtypeFormModal({
  item,
  onSave,
  onClose,
}: {
  item: MovementSubtype | null;
  onSave: (data: { movementTypeId: string; code: string; name: string; description?: string; requiresReference: boolean }) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const isEditing = !!item;
  const [movementTypeId, setMovementTypeId] = useState(item?.movementTypeId ?? "");
  const [code, setCode] = useState(item?.code ?? "");
  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [requiresReference, setRequiresReference] = useState(item?.requiresReference ?? false);
  const [types, setTypes] = useState<MovementType[]>([]);

  useEffect(() => { listMovementTypes().then(all => setTypes(all.filter(t => t.status))).catch(() => {}); }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const isValid = (isEditing || (movementTypeId && code.trim().length >= 2)) && name.trim().length >= 3;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    onSave({ movementTypeId, code: code.trim().toUpperCase(), name: name.trim(), description: description.trim() || undefined, requiresReference });
  };

  const activeType = types.find(t => t.id === item?.movementTypeId);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
          {isEditing ? t.movementSubtype.editSubtype : t.movementSubtype.addSubtype}
        </h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {isEditing ? (
            <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#aaa" }}>
              {t.movementSubtype.typeLabel}: <strong style={{ color: "#555" }}>{activeType?.name ?? "—"}</strong>
              {" · "}{t.movementSubtype.codeLabel}: <strong style={{ color: "#555" }}>{item.code}</strong>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>
                  {t.movementSubtype.typeLabel} <span style={{ color: colors.primary }}>*</span>
                </label>
                <select value={movementTypeId} onChange={e => setMovementTypeId(e.target.value)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: movementTypeId ? "#111" : "#aaa", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" }}>
                  <option value="">{t.movementSubtype.typePlaceholder}</option>
                  {types.map(mt => <option key={mt.id} value={mt.id}>{mt.code === "IN" ? t.movementSubtype.typeIn : mt.code === "OUT" ? t.movementSubtype.typeOut : mt.name}</option>)}
                </select>
              </div>
              <Field label={t.movementSubtype.codeLabel} value={code} onChange={setCode} placeholder={t.movementSubtype.codePlaceholder} required />
            </>
          )}

          <Field label={t.movementSubtype.nameLabel} value={name} onChange={setName} placeholder={t.movementSubtype.namePlaceholder} required />

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.movementSubtype.descriptionLabel}</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t.movementSubtype.descriptionPlaceholder} rows={2} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box", resize: "vertical" }} />
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>
              {t.movementSubtype.requiresRefLabel}
            </span>
            <button
              type="button"
              onClick={() => setRequiresReference(v => !v)}
              style={{
                width: "44px", height: "24px", borderRadius: "9999px", border: "none",
                background: requiresReference ? colors.primary : "#ddd",
                cursor: "pointer", position: "relative", transition: `background ${transitions.fast}`,
              }}
            >
              <span style={{ position: "absolute", top: "3px", left: requiresReference ? "23px" : "3px", width: "18px", height: "18px", borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: `left ${transitions.fast}` }} />
            </button>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button type="button" onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.movementSubtype.cancel}</button>
            <button type="submit" disabled={!isValid} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: isValid ? "#111" : "#ccc", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: isValid ? "pointer" : "not-allowed", transition: `background ${transitions.fast}` }} onMouseEnter={e => { if (isValid) (e.currentTarget as HTMLButtonElement).style.background = "#333"; }} onMouseLeave={e => { if (isValid) (e.currentTarget as HTMLButtonElement).style.background = "#111"; }}>{t.movementSubtype.save}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required = false }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; required?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>
        {label} {required && <span style={{ color: colors.primary }}>*</span>}
      </label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box", transition: `border-color ${transitions.fast}` }} onFocus={e => (e.currentTarget.style.borderColor = "#111")} onBlur={e => (e.currentTarget.style.borderColor = "#e5e5e5")} />
    </div>
  );
}
